/**
 * Top-down camera that follows the local player ship.
 * Direct camera management (no OrbitControls) eliminates the jitter
 * caused by OrbitControls.update() fighting with manual position setting.
 * Scroll wheel controls zoom height with smooth interpolation.
 *
 * Camera position uses the SAME velocity-based sub-tick extrapolation formula
 * as PlayerShip: visualPos = physicsPos + velocity * alpha * TICK_DELTA.
 * This ensures zero relative jitter between ship and static world objects
 * (like the map border), which was the root cause of Firefox stutter.
 *
 * Camera lead: offsets slightly in the ship's velocity direction so the
 * player can see more of what's ahead. Lerped smoothly to avoid jerks.
 */

import { useRef, useEffect } from 'react';
import { PerspectiveCamera } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { TICK_DELTA } from '@warmancer/shared';
import { useGameStore } from '../../stores/gameStore';

const CAMERA_HEIGHT_DEFAULT = 50;
const CAMERA_FOV = 60;
const ZOOM_LERP_SPEED = 8;
const ZOOM_WHEEL_SENSITIVITY = 0.05;

export const CAMERA_ZOOM_MIN = 10;
export const CAMERA_ZOOM_MAX = 200;

// Camera lead: offset in velocity direction
const CAMERA_LEAD_FACTOR = 0.12; // seconds of velocity to offset
const CAMERA_LEAD_LERP = 5.0; // how fast the lead catches up

// Reusable vector to avoid per-frame allocation
const lookAtTarget = new THREE.Vector3();

export function TopDownCamera() {
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const initialized = useRef(false);

  // Zoom: target (where scroll is pointing) and current (smoothly follows)
  const zoomTarget = useRef(CAMERA_HEIGHT_DEFAULT);
  const zoomCurrent = useRef(CAMERA_HEIGHT_DEFAULT);

  // Camera lead (lerped smoothly)
  const leadX = useRef(0);
  const leadZ = useRef(0);

  // Handle scroll wheel zoom outside of React Three Fiber's event system
  // to ensure it works regardless of pointer capture state.
  useEffect(() => {
    function handleWheel(e: WheelEvent) {
      // Prevent page scroll
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1 : -1;
      zoomTarget.current = Math.max(
        CAMERA_ZOOM_MIN,
        Math.min(CAMERA_ZOOM_MAX, zoomTarget.current + delta * zoomTarget.current * ZOOM_WHEEL_SENSITIVITY),
      );
    }
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  useFrame((_, delta) => {
    const camera = cameraRef.current;
    if (!camera) return;

    // Store camera ref in gameStore for mouse-to-world projection
    if (!useGameStore.getState().cameraRef) {
      useGameStore.getState().setCameraRef(camera);
    }

    // Read ship position directly from store (avoids React prop staleness)
    const ship = useGameStore.getState().localShip;
    const alpha = useGameStore.getState().renderAlpha;

    // Velocity-based sub-tick extrapolation — SAME formula as PlayerShip.
    // This ensures camera and ship move in exact lockstep, eliminating
    // relative jitter between the ship and static world objects (map border).
    const shipX = ship ? ship.x + ship.vx * alpha * TICK_DELTA : 0;
    const shipZ = ship ? ship.y + ship.vy * alpha * TICK_DELTA : 0;

    // Camera lead: offset in velocity direction
    const targetLeadX = ship ? ship.vx * CAMERA_LEAD_FACTOR : 0;
    const targetLeadZ = ship ? ship.vy * CAMERA_LEAD_FACTOR : 0;
    const tLead = Math.min(1, CAMERA_LEAD_LERP * delta);
    leadX.current += (targetLeadX - leadX.current) * tLead;
    leadZ.current += (targetLeadZ - leadZ.current) * tLead;

    // First frame: snap to position
    if (!initialized.current) {
      zoomCurrent.current = zoomTarget.current;
      leadX.current = targetLeadX;
      leadZ.current = targetLeadZ;
      camera.position.set(shipX + leadX.current, zoomCurrent.current, shipZ + leadZ.current);
      lookAtTarget.set(shipX + leadX.current, 0, shipZ + leadZ.current);
      camera.up.set(0, 0, -1);
      camera.lookAt(lookAtTarget);
      initialized.current = true;
      return;
    }

    // Camera follow: snap to extrapolated ship position (skip when camera follow is off)
    const follow = useGameStore.getState().cameraFollow;

    // Smooth zoom
    const tZoom = Math.min(1, ZOOM_LERP_SPEED * delta);
    zoomCurrent.current += (zoomTarget.current - zoomCurrent.current) * tZoom;

    // Position camera directly above the ship with lead offset (or stay if follow is off)
    if (follow) {
      const camX = shipX + leadX.current;
      const camZ = shipZ + leadZ.current;
      camera.position.set(camX, zoomCurrent.current, camZ);
      lookAtTarget.set(camX, 0, camZ);
    } else {
      camera.position.y = zoomCurrent.current;
    }
    camera.lookAt(lookAtTarget);
  });

  return (
    <PerspectiveCamera
      ref={cameraRef}
      makeDefault
      fov={CAMERA_FOV}
    />
  );
}
